import { useEffect, useRef, useState } from "react";
import { useQuery } from "react-apollo";

import nanoid from "nanoid";

import { useStore } from "../providers/AppProvider";
import { SETTING_SEASON_YEAR } from "../utils/settings";

import { GET_DATA_SOURCES2 } from "../../../queries";

import { DataSourceLayerMeta, DataSourceRasterLayer, DataSourceRasterLayerExtended} from "../types/types";
import { getTileUrlTemplate } from "../utils/dataSourceUtils";

import { MAPBOX_SATELLITE } from '../components/Map/styles/mapbox-satellite';
import ESRI_BASE_LABELS from '../components/Map/styles/esri-labels.json';

// Global org IDs for shared/public Level 1 data sources
const GLOBAL_ORG_IDS = [
    "fd8ce854-9f8e-11ea-a22c-02a6f2671be5",
    "c152cee1-c250-42e9-9e1c-aa0cb5525f39",
];

export const useRasterDataSources = (config: DataSourceLayerMeta[]) => {
    const {settings, orgId} = useStore();

    const [dataSources, setDataSources] = useState<DataSourceRasterLayer[]>([]);
    const [year, setYear] = useState<string|null>(null);

    const {data, loading} = useQuery(GET_DATA_SOURCES2, { fetchPolicy: "no-cache" });

    const tsRef = useRef<string | null>(null);
    const seasonYear = settings[SETTING_SEASON_YEAR];

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        setYear(seasonYear);
    },[seasonYear]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        console.log("012524 useRasterDataSources", settings)
        console.log("012524 useRasterDataSources", loading)
        console.log("012524 useRasterDataSources", data)
        if(!loading && data) { 
            let settingsTs = null;
            if('indices_date' in settings) {
                const obj = JSON.parse(settings['indices_date']);
                settingsTs = obj.end_date;
            }
            console.log("012524 useRasterDataSources tsRef.current", tsRef.current)
            console.log("012524 useRasterDataSources settingsTs", settingsTs)
            if(tsRef.current !== settingsTs) {
                tsRef.current = settingsTs;
            } else {
                if(!tsRef.current && !settingsTs) {
                    // if these are both null then the timeline probably wasnt engaged yet
                }else{
                    if(dataSources.length) {
                        console.log("012524 useRasterDataSources returning")
                        return;
                    }
                }
            }

            //
            console.log("012524 config", config)
            const sourceIds = config.map((c: DataSourceLayerMeta) => c.id);
            console.log("012524 sourceIds", sourceIds)
            console.log("012524 data", data)

            // Log the raw data structure to see what fields are actually returned
            console.log("012524 RAW DATA:", data);
            console.log("012524 FIRST SOURCE (all fields):", data.syncarto_app_data_source[0]);
            console.log("012524 TOTAL SOURCES COUNT:", data.syncarto_app_data_source.length);

            /**
             * LEVEL 1 FILTERING LOGIC:
             * 1. CRITICAL: Exclude ANY source with level > 1 (regardless of org)
             * 2. Include sources from GLOBAL_ORG_ID where level is 1, null, or undefined
             * 3. Include sources from user's org where level === 1 or null/undefined
             * 4. If duplicate names exist, prefer user's org version over global
             */
            const level1Sources = data.syncarto_app_data_source.filter((source: any) => {
                const isGlobalOrg = GLOBAL_ORG_IDS.includes(source.org_id);
                const isUserOrg = source.org_id === orgId;
                const sourceLevel = source.level;

                // Bypass level filter for sources explicitly listed in the config (e.g., Natural Color / UAS Imagery).
                // The sourceIds filter below already limits to only config-listed collection_ids.
                const isExplicitlyRequested =
                    sourceIds.includes(source.collection_id!) ||
                    (sourceIds.includes(source.collection_id!.replace("-rgb", "-rgbn")) &&
                        source.title === "High Res. Normal Color");
                if (isExplicitlyRequested) return true;

                // CRITICAL: Explicitly exclude ANY source with level > 1 (before any inclusion logic)
                if (sourceLevel && sourceLevel > 1) {
                    console.log("012524 EXCLUDING (level > 1):", source.title, "level:", sourceLevel, "org:", source.org_id);
                    return false;
                }

                // Include global org sources where level is 1, null, or undefined
                if (isGlobalOrg && (sourceLevel === 1 || sourceLevel === null || sourceLevel === undefined)) {
                    return true;
                }

                // Include user org sources where level is 1, null, or undefined
                if (isUserOrg && (sourceLevel === 1 || sourceLevel === null || sourceLevel === undefined)) {
                    return true;
                }

                return false;
            });

            console.log("012524 LEVEL 1 SOURCES (before deduplication):", level1Sources.length);

            // Deduplicate: if same title exists in both global and user org, keep user org version
            const deduplicatedSources = level1Sources.reduce((acc: any[], source: any) => {
                const existingIndex = acc.findIndex((s: any) => s.title === source.title);

                if (existingIndex === -1) {
                    // Title doesn't exist yet, add it
                    acc.push(source);
                } else {
                    // Title exists, prefer user org over global org
                    const existing = acc[existingIndex];
                    if (source.org_id === orgId && GLOBAL_ORG_IDS.includes(existing.org_id)) {
                        // Replace global version with user org version
                        acc[existingIndex] = source;
                    }
                    // Otherwise keep existing (either both are global, both are user org, or existing is already user org)
                }

                return acc;
            }, []);

            console.log("012524 LEVEL 1 SOURCES (after deduplication):", deduplicatedSources.length);

            const sources = deduplicatedSources.filter((source: any) => {
                // Add additional matching condition for UAS Imagery (nominally on Olam) which has a different collection_id suffix (-rgb instead of -rgbn)
                return (sourceIds.includes(source.collection_id!) || (sourceIds.includes(source.collection_id!.replace("-rgb","-rgbn")) && source.title === "High Res. Normal Color"))
                //return sourceIds.includes(source.collection_id!) 
            });
            console.log("012524 sources", sources)
            sources.push(ESRI_BASE_LABELS);
            sources.push(MAPBOX_SATELLITE);
            const layers: DataSourceRasterLayer[] = sources.map((source: DataSourceRasterLayerExtended) => {
                console.log("062723 source", source)
                const dataSourceMetaFiltered = config.filter(s => {
                    if (s.name === "UAS Imagery"){
                        //return (s.id === source.collection_id && source.title === "High Res. Normal Color");
                        return (source.title === "High Res. Normal Color");
                    }else{
                        return s.id === source.collection_id;
                    }
                });
                let dataSourceMeta = dataSourceMetaFiltered[0];
                if (dataSourceMetaFiltered.length > 1){
                    const dataSourceMetaFind = dataSourceMetaFiltered.find(s => {
                        return s.disambiguate === source.title;
                    });
                    if (dataSourceMetaFind){ dataSourceMeta = dataSourceMetaFind };
                } 
                let _uuid = nanoid(8);               
                /*if (source.collection_id === 'NAIP'){
                    _uuid = "NAIP_678" // Use an arbitrary but consistent uuid here in order to get react to NOT re-render NAIP aerial upon date update.
                }*/
                if (source.collection_id === 'ESRI'){
                    _uuid = "ESRI_678" // Use an arbitrary but consistent uuid here in order to get react to NOT re-render ESRI satellite upon date update.
                }
                if (source.collection_id === 'ESRI_LABELS'){
                    _uuid = "ESRI_LABELS_678" // Use an arbitrary but consistent uuid here in order to get react to NOT re-render ESRI satellite upon date update.
                }
                if (dataSourceMeta?.date && year){
                    dataSourceMeta.date = year;
                }
                const dataSourceLayer: DataSourceRasterLayer = {
                    name: dataSourceMeta?.name ?? source.title,
                    id: source.collection_id!,
                    uuid: _uuid,
                    min_zoom: dataSourceMeta ? dataSourceMeta.minZoom : 0,
                    max_zoom: dataSourceMeta ? dataSourceMeta.maxZoom : 24,
                    is_visible: dataSourceMeta?.visible!,
                    type: dataSourceMeta?.type!,
                    order: dataSourceMeta?.order!,
                    display: dataSourceMeta?.display,
                    tiles: source.stac_address 
                        ? getTileUrlTemplate(
                            source,
                            source.stac_address!, 
                            source.collection_id!,
                            'footprint_id',
                            dataSourceMeta?.date ? `${year}-01-01` : source.min_date,
                            dataSourceMeta?.date ? `${year}-12-31` : (tsRef.current ? tsRef.current : source.max_date),
                            source.date_display_resolution!,
                            dataSourceMeta?.date ? "template" : "template-singledate-p1",
                            null,
                            "global",
                            settings.season_year
                        ) 
                        : source.tile_urls,
                    source: {...source},
                    dataSourceMeta: dataSourceMeta
                };

                return dataSourceLayer;
                
            });
            setDataSources(layers);
        } // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, settings]);

    return { dataSources, loading };
}
